"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface PromoBannerConfig {
  id: string;
  enabled: boolean;
  message: string;
  linkText: string;
  linkUrl: string;
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  dismissible: boolean;
  position: 'top' | 'bottom';
  startDate?: string;
  endDate?: string;
}

const DEFAULT_BANNER: PromoBannerConfig = {
  id: 'default',
  enabled: false,
  message: '🔥 Summer Sale! Get 20% off all products',
  linkText: 'Shop Now',
  linkUrl: '/shop',
  backgroundColor: '#3182CE',
  textColor: '#FFFFFF',
  buttonColor: '#38A169',
  buttonTextColor: '#FFFFFF',
  dismissible: true,
  position: 'top'
};

export default function PromoBanner() {
  const [banner, setBanner] = useState<PromoBannerConfig | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        // Fetch site configuration from the API
        const response = await fetch('/api/admin/config');
        const data = await response.json();
        
        // Extract banner configuration from the branding settings
        const bannerConfig = data.branding?.promoBanner || DEFAULT_BANNER;
        
        // Check if this banner was previously dismissed by the user
        const localDismissed = localStorage.getItem(`banner-dismissed-${bannerConfig.id}`);
        if (localDismissed) {
          setDismissed(true);
        }
        
        // Check if banner should be shown based on dates and enabled status
        const now = new Date();
        const showBanner = bannerConfig.enabled && 
          (!bannerConfig.startDate || new Date(bannerConfig.startDate) <= now) &&
          (!bannerConfig.endDate || new Date(bannerConfig.endDate) >= now);
        
        if (showBanner) {
          setBanner(bannerConfig);
        }
      } catch (error) {
        console.error('Error fetching promo banner:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Don't render anything if no banner or banner was dismissed
  if (isLoading || !banner || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    if (banner.dismissible) {
      localStorage.setItem(`banner-dismissed-${banner.id}`, 'true');
    }
  };

  const positionClass = banner.position === 'top' 
    ? 'top-0' 
    : 'bottom-0';

  return (
    <div 
      className={`fixed left-0 right-0 z-50 ${positionClass}`}
      style={{ backgroundColor: banner.backgroundColor }}
    >
      <div className="container mx-auto px-4 py-2 md:py-3 flex flex-wrap md:flex-nowrap items-center justify-between">
        <p 
          className="text-sm md:text-base font-medium mr-4 flex-grow text-center md:text-left"
          style={{ color: banner.textColor }}
        >
          {banner.message}
        </p>
        
        <div className="flex items-center mt-2 md:mt-0 w-full md:w-auto justify-center md:justify-start">
          <Link href={banner.linkUrl}>
            <Button 
              className="text-xs md:text-sm px-3 py-1 rounded-md mr-4"
              style={{ 
                backgroundColor: banner.buttonColor,
                color: banner.buttonTextColor 
              }}
            >
              {banner.linkText}
            </Button>
          </Link>
          
          {banner.dismissible && (
            <button 
              onClick={handleDismiss}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Dismiss"
            >
              <X size={16} style={{ color: banner.textColor }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
